/**
 * 遺伝子解析の作業面（§0.3.3）。Phase 2 で解析ドックを同じ面に足す。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FeatureIndex, isSequenceReadable } from '@kensuke/genome-core';
import GeneAnalysisAnalysisPanel from './GeneAnalysisAnalysisPanel';
import GeneAnalysisCircularMap from './GeneAnalysisCircularMap';
import GeneAnalysisEmptyState from './GeneAnalysisEmptyState';
import GeneAnalysisSelectionCard from './GeneAnalysisSelectionCard';
import GeneAnalysisStatusBar from './GeneAnalysisStatusBar';
import GeneAnalysisToolbar from './GeneAnalysisToolbar';
import GenomeMapCanvas from './GenomeMapCanvas';
import SequenceCanvas from './SequenceCanvas';
import { defaultAnalysisTab, type AnalysisTab } from './geneAnalysisAnalysis';
import { createDemoSequenceFile } from './geneAnalysisDemoSequence';
import { useGeneAnalysisAnalysis } from './useGeneAnalysisAnalysis';
import { useGeneAnalysisWorkspace } from './useGeneAnalysisWorkspace';
import './geneAnalysisWorkspace.css';

export default function GeneAnalysisWorkspace() {
  const { t } = useTranslation();
  const workspace = useGeneAnalysisWorkspace();
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [surfaceWidth, setSurfaceWidth] = useState(1200);
  const [dropActive, setDropActive] = useState(false);
  const [tab, setTab] = useState<AnalysisTab>('search');

  useEffect(() => {
    const element = surfaceRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setSurfaceWidth(width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDropActive(false);
      if (workspace.importState.running || workspace.restoring) return;
      const file = event.dataTransfer.files?.[0];
      if (file) void workspace.importFile(file);
    },
    [workspace]
  );

  const { activeMeta, sequence, importState, view, displayOptions: display, restoring } = workspace;
  const showSequencePane = Boolean(activeMeta && sequence && isSequenceReadable(view, surfaceWidth));
  const visibleRange = showSequencePane ? workspace.renderedWindow ?? view : view;

  useEffect(() => {
    if (activeMeta) setTab(defaultAnalysisTab(activeMeta.length, activeMeta.topology));
  }, [activeMeta?.sequenceId, activeMeta?.length, activeMeta?.topology]);

  const analysis = useGeneAnalysisAnalysis({
    sequence,
    features: workspace.features,
    view: visibleRange,
    selection: workspace.selection,
    tab,
    sequenceId: workspace.activeSequenceId,
  });

  const { reportRenderedWindow } = workspace;
  useEffect(() => {
    if (!showSequencePane) reportRenderedWindow(null);
  }, [showSequencePane, reportRenderedWindow]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') workspace.setSelection(null);
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void workspace.saveProject();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [workspace]);

  const mapFeatures = useMemo(
    () => [...workspace.features, ...analysis.predictedFeatures],
    [workspace.features, analysis.predictedFeatures]
  );
  const mapIndex = useMemo(
    () =>
      activeMeta
        ? new FeatureIndex(mapFeatures, activeMeta.length, { topology: activeMeta.topology })
        : null,
    [activeMeta, mapFeatures]
  );

  return (
    <div
      ref={surfaceRef}
      className={`ga-surface${display.theme === 'dark' ? ' ga-surface-dark' : ''}${
        dropActive ? ' ga-surface-drop' : ''
      }`}
      data-testid="gene-analysis-workspace"
      data-theme={display.theme}
      onDragOver={(event) => {
        event.preventDefault();
        if (!importState.running && !restoring) setDropActive(true);
      }}
      onDragLeave={() => setDropActive(false)}
      onDrop={handleDrop}
    >
      {activeMeta && sequence ? (
        <>
          <GeneAnalysisToolbar
            sequences={workspace.sequences}
            activeSequenceId={workspace.activeSequenceId}
            onSelectSequence={workspace.setActiveSequenceId}
            view={view}
            onViewChange={workspace.setView}
            genomeLength={activeMeta.length}
            baseMode={showSequencePane}
            display={display}
            onDisplayChange={workspace.updateDisplayOptions}
            onImportFile={(file) => void workspace.importFile(file)}
            importing={importState.running}
            onSave={() => void workspace.saveProject()}
          />

          {importState.running && (
            <ProgressBar
              className="ga-progress"
              now={
                importState.totalBytes > 0
                  ? Math.round((importState.bytesRead / importState.totalBytes) * 100)
                  : 0
              }
              label={`${importState.basesRead.toLocaleString()} bp`}
            />
          )}

          {importState.error && (
            <Alert variant="danger" className="ga-alert">
              {importState.error}
            </Alert>
          )}
          {importState.warnings.length > 0 && (
            <Alert variant="warning" className="ga-alert">
              {importState.warnings.slice(0, 3).join(' / ')}
            </Alert>
          )}

          <div className="ga-workbench">
            <div className="ga-workbench-main">
              {tab === 'plasmid' && activeMeta.topology === 'circular' ? (
                <GeneAnalysisCircularMap
                  length={activeMeta.length}
                  features={mapFeatures}
                  selection={workspace.selection}
                  cuts={analysis.result.cuts}
                  onSelect={workspace.setSelection}
                  onFeatureActivate={workspace.selectFeature}
                  theme={display.theme}
                />
              ) : (
                <GenomeMapCanvas
                  length={activeMeta.length}
                  topology={activeMeta.topology}
                  featureIndex={mapIndex}
                  view={view}
                  visibleRange={visibleRange}
                  onViewChange={workspace.setView}
                  selection={workspace.selection}
                  onSelectionChange={workspace.setSelection}
                  onFeatureActivate={workspace.selectFeature}
                  theme={display.theme}
                  overlays={analysis.overlays}
                />
              )}

              {showSequencePane ? (
                <SequenceCanvas
                  sequence={sequence}
                  view={view}
                  onViewChange={workspace.setView}
                  selection={workspace.selection}
                  onSelectionChange={workspace.setSelection}
                  display={display}
                  onFontSizeChange={(fontSize) => workspace.updateDisplayOptions({ fontSize })}
                  onWindowChange={reportRenderedWindow}
                  overlays={analysis.overlays}
                />
              ) : (
                <div className="ga-zoom-hint" data-testid="gene-analysis-zoom-hint">
                  {t('geneAnalysis.hint.zoom_for_bases')}
                </div>
              )}

              {workspace.selection && workspace.selection.end - workspace.selection.start >= 2 && (
                <GeneAnalysisSelectionCard
                  sequence={sequence}
                  selection={workspace.selection}
                  hidePrimers={tab === 'primer'}
                />
              )}
            </div>

            <GeneAnalysisAnalysisPanel
              tab={tab}
              onTabChange={setTab}
              meta={activeMeta}
              analysis={analysis}
              features={workspace.features}
              onJump={workspace.jumpTo}
              onSelect={workspace.setSelection}
              onAddFeature={workspace.addFeature}
              onDeleteFeature={workspace.deleteFeature}
              onRename={workspace.renameActive}
              onTopologyChange={workspace.setTopology}
              onExport={(kind) => void workspace.exportActive(kind)}
              selection={workspace.selection}
            />
          </div>

          <GeneAnalysisStatusBar
            meta={activeMeta}
            sequence={sequence}
            view={visibleRange}
            selection={workspace.selection}
            featureCount={workspace.features.length}
            baseMode={showSequencePane}
          />
        </>
      ) : restoring ? (
        <div className="ga-importing" data-testid="gene-analysis-restoring">
          <p className="mb-2">{t('geneAnalysis.empty.restoring')}</p>
        </div>
      ) : (
        <>
          {importState.running ? (
            <div className="ga-importing" data-testid="gene-analysis-importing">
              <p className="mb-2">
                {t('geneAnalysis.empty.importing', {
                  name: importState.sequenceName ?? '',
                })}
              </p>
              <ProgressBar
                now={
                  importState.totalBytes > 0
                    ? Math.round((importState.bytesRead / importState.totalBytes) * 100)
                    : 0
                }
                label={`${importState.basesRead.toLocaleString()} bp`}
              />
            </div>
          ) : (
            <GeneAnalysisEmptyState
              onImportFile={(file) => void workspace.importFile(file)}
              onOpenDemo={() => void workspace.importFile(createDemoSequenceFile())}
              importing={importState.running}
            />
          )}
          {importState.error && (
            <Alert variant="danger" className="ga-alert">
              {importState.error}
            </Alert>
          )}
        </>
      )}
    </div>
  );
}
